// components/ManageSimulations.js
import React, { useState, useEffect } from 'react';
import { Typography, Button, List, ListItem, ListItemText, Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import { getIdToken } from './Firebase';

const ManageSimulations = () => {
  const [competitions, setCompetitions] = useState([]);
  const [simulations, setSimulations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getIdToken(false);
        const competitionsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/admin/simulations`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        const fetchedCompetitions = competitionsResponse.data;

        if (!Array.isArray(fetchedCompetitions) || fetchedCompetitions.length === 0) {
          throw new Error('Nieprawidłowy format danych z API (brak tablicy rozgrywek).');
        }

        setCompetitions(fetchedCompetitions);

        const simsData = fetchedCompetitions.reduce((acc, comp) => {
          acc[comp.competition_id] = comp.simulations || [];
          return acc;
        }, {});
        setSimulations(simsData);
        setLoading(false);
      } catch (err) {
        setError(`Błąd podczas pobierania danych: ${err.message}`);
        console.error('Błąd API:', err);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteSimulation = async (competitionId, simulationId) => {
    try {
      let token = null;
      try {
        const { getIdToken } = await import('./Firebase');
        token = await getIdToken(false);
      } catch (tokenErr) {
        console.warn('Nie udało się pobrać tokena:', tokenErr);
      }

      await axios.delete(`${process.env.REACT_APP_API_URL}/api/simulations/${simulationId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setSimulations(prev => ({
        ...prev,
        [competitionId]: prev[competitionId].filter(sim => sim.simulation_id !== simulationId)
      }));
      alert('Symulacja usunięta pomyślnie.');
    } catch (err) {
      console.error('Błąd podczas usuwania symulacji:', err);
      alert('Błąd podczas usuwania symulacji. Sprawdź konsolę dla szczegółów.');
    }
  };

  const API_HEADERS = {
  'Content-Type': 'application/json',
  'X-API-Key': process.env.REACT_APP_MAKE_API_KEY,
};

const apiCall = async (endpoint, data = {}) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_API_URL}${endpoint}`,
      data,
      { headers: API_HEADERS }
    );
    return { success: true };
  } catch (err) {
    console.error(`Błąd ${endpoint}:`, err);
    const msg = err.response?.data?.error || err.message;
    alert(`Błąd: ${msg}`);
    return { success: false, error: msg };
  }
};

const handleFetchData = (id) => apiCall('/api/scrap-data', { competitionId: id });
const handleCalculateSimulation = (id) => apiCall('/api/simulate', { competitionId: id });

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', padding: '10px' }}>
        <CircularProgress />
        <Typography variant="body2">Ładowanie...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', padding: '10px' }}>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '10px' }}>
      <Typography variant="h5" gutterBottom>
        Zarządzanie Symulacjami
      </Typography>
      {competitions.map((competition) => (
        <Box key={competition.competition_id} sx={{ mb: 2, border: '1px solid #ccc', p: 1, borderRadius: 2 }}>
          <Typography variant="subtitle1">
            {competition.name} (Sezon: {competition.season} {competition.group && competition.group.trim() !== ' ' ? `(Grupa: ${competition.group.trim()})` : ''})
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleFetchData(competition.competition_id)}
            >
              Pobierz dane
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => handleCalculateSimulation(competition.competition_id)}
            >
              Oblicz symulację
            </Button>
          </Box>

          <List dense>
            {simulations[competition.competition_id] && simulations[competition.competition_id].length > 0 ? (
              simulations[competition.competition_id].map((simulation) => (
                <ListItem
                  key={simulation.simulation_id}
                  secondaryAction={
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteSimulation(competition.competition_id, simulation.simulation_id)}
                    >
                      Usuń
                    </Button>
                  }
                >
                  <ListItemText
                    primary={`#${simulation.simulation_id}`}
                    secondary={`Data: ${new Date(simulation.created_at).toLocaleString()}`}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="Brak symulacji." />
              </ListItem>
            )}
          </List>
        </Box>
      ))}
    </Box>
  );
};

export default ManageSimulations;
